import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, MapPin, Facebook, Linkedin, Instagram, MessageCircle, ArrowUp, Youtube, Twitter } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const navItems = [
  { label: 'Home', path: '/', anchor: null },
  { label: 'About Us', path: '/about', anchor: 'about' },
  { label: 'Services', path: '/services', anchor: 'services' },
  { label: 'Products', path: '/products', anchor: null },
  { label: 'Portfolio', path: '/portfolio', anchor: 'portfolio' },
  { label: 'Testimonials', path: '/testimonials', anchor: 'testimonials' },
  { label: 'Blog', path: '/blog', anchor: null },
  { label: 'Contact', path: '/contact', anchor: 'contact' },
];

// Custom Designed Logo Component
const Logo = ({ className = "h-10", white = false }: { className?: string, white?: boolean }) => (
  <div className="flex items-center gap-2 select-none" title="EffTech Limited Logo">
    {/* Geometric ET Mark - Specific Design Request */}
    <svg 
      className={`${className} w-auto ${white ? 'text-white' : 'text-primary-800'}`} 
      viewBox="0 0 65 60" 
      fill="none" 
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
       {/* E Shape - Thinner strokes (10px bars, 11px spine), Curved Top-Left */}
       <path d="M10 0H60V10H11V21H35V31H11V42H35V52H0V10Q0 0 10 0Z" fill="currentColor"/>
       
       {/* T Shape - Thinner strokes, dropped to align with middle E bar, extended bottom */}
       <path d="M40 21H60V31H51V60H40V21Z" fill="currentColor"/>
    </svg>
    
    {/* Stacked Text */}
    <div className="flex flex-col justify-center h-full ml-1">
      <span className={`text-2xl font-bold leading-[0.8] tracking-tight ${white ? 'text-white' : 'text-primary-800'}`}>Eff</span>
      <span className={`text-2xl font-bold leading-[0.8] tracking-tight ${white ? 'text-white' : 'text-primary-800'}`}>Tech</span>
    </div>
  </div>
);

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  // Dynamic Page Titles
  useEffect(() => {
    const routeTitles: Record<string, string> = {
      '/': 'Home',
      '/about': 'About Us',
      '/services': 'Services',
      '/portfolio': 'Portfolio',
      '/contact': 'Contact Us',
      '/privacy': 'Privacy Policy',
      '/terms': 'Terms of Service'
    };

    const currentTitle = routeTitles[location.pathname] || 'IT Solutions';
    document.title = `${currentTitle} | EffTech Limited`;
  }, [location]);

  // Handle scroll effect for navbar and scroll-to-top button
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
      setShowScrollTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change and scroll to top
  useEffect(() => {
    setIsMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const isActive = (path: string) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  const handleNavClick = (e: React.MouseEvent, item: typeof navItems[0]) => {
    // If we're on the home page and the item has an anchor, scroll to it
    if (location.pathname === '/' && item.anchor) {
      e.preventDefault();
      const element = document.getElementById(item.anchor);
      if (element) {
        const headerOffset = 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }
    // Otherwise, let the Link component handle normal navigation
  };

  return (
    <div className="flex flex-col min-h-screen relative">
      {/* Top Bar */}
      <div className="bg-primary-950 text-white py-2.5 text-xs sm:text-sm px-3 sm:px-4 hidden md:block border-b border-primary-900 before:content-none after:content-none">
        <div className="container flex flex-wrap justify-between items-center gap-2 before:content-none after:content-none">
          <div className="flex items-center space-x-8">
            <div className="flex items-center space-x-2 group cursor-pointer">
              <Phone size={14} className="text-accent-400 group-hover:text-white transition-colors" />
              <span className="opacity-90 group-hover:opacity-100 transition-opacity">(+231) 770-014-544</span>
            </div>
            <div className="flex items-center space-x-2 group">
              <Mail size={14} className="text-accent-400 group-hover:text-white transition-colors" />
              <a href="mailto:efftech1@gmail.com" className="opacity-90 hover:opacity-100 transition-opacity hover:text-accent-200">efftech1@gmail.com</a>
            </div>
          </div>
          <div className="flex items-center space-x-2 text-gray-400">
             <MapPin size={14} className="text-primary-600"/>
             <span>Capitol Hill, Monrovia</span>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <header className={`sticky top-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-lg py-2' : 'bg-white/90 backdrop-blur-md py-4 border-b border-gray-100'}`}>
        <div className="container px-3 sm:px-4 flex justify-between items-center gap-3">
          <Link to="/" className="flex items-center space-x-2 group" aria-label="Go to Homepage">
            <Logo className="h-10" />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center space-x-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={(e) => handleNavClick(e, item)}
                className={`relative px-4 py-2 font-medium text-sm uppercase tracking-wide transition-colors group ${
                  isActive(item.path) ? 'text-primary-700 font-bold' : 'text-gray-600 hover:text-primary-600'
                }`}
              >
                {item.label}
                <span className={`absolute bottom-0 left-0 w-full h-0.5 bg-primary-600 transform origin-left transition-transform duration-300 ${isActive(item.path) ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'}`}></span>
              </Link>
            ))}
            
            <div className="ml-4 pl-4 border-l border-gray-200">
                <Link
                  to="/contact"
                  onClick={(e) => handleNavClick(e, { label: 'Contact', path: '/contact', anchor: 'contact' })}
                  className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2.5 rounded-full font-bold text-sm transition-all transform hover:scale-105 shadow-md hover:shadow-lg"
                >
                  Get Quote
                </Link>
            </div>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-gray-700 focus:outline-none p-2.5 rounded-lg hover:bg-gray-100 min-h-11 min-w-11 flex items-center justify-center"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle Navigation Menu"
          >
            {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Nav Dropdown */}
        {isMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 shadow-xl absolute w-full left-0 top-full h-[calc(100vh-4rem)] overflow-y-auto pb-24">
            <div className="flex flex-col p-3 sm:p-4 space-y-2">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={(e) => {
                    handleNavClick(e, item);
                    setIsMenuOpen(false);
                  }}
                  className={`block py-3 px-4 rounded-xl text-lg font-medium ${
                    isActive(item.path) ? 'bg-primary-50 text-primary-700' : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
              
              <div className="pt-4 mt-2 border-t border-gray-100">
                <Link 
                  to="/contact"
                  onClick={(e) => {
                    handleNavClick(e, { label: 'Contact', path: '/contact', anchor: 'contact' });
                    setIsMenuOpen(false);
                  }}
                  className="block w-full text-center bg-primary-600 text-white py-3 rounded-xl font-bold"
                >
                    Get a Quote
                </Link>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-grow">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-gray-950 text-gray-300 pt-20 pb-10 border-t border-primary-900">
        <div className="container px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
            {/* Company Info */}
            <div>
              <div className="mb-8">
                <Logo white />
              </div>
              <p className="text-gray-400 mb-8 leading-relaxed text-sm">
                Bridging the digital gap through modern IT services, innovative technology systems, and professional training across Liberia.
              </p>
              <div className="flex space-x-3">
                <a href="https://www.facebook.com/profile.php?id=61584935128391" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="bg-gray-800 p-2.5 rounded-lg hover:bg-primary-600 hover:text-white text-gray-400 transition-all transform hover:-translate-y-1"><Facebook size={18} /></a>
                <a href="https://www.instagram.com/efftech1/" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="bg-gray-800 p-2.5 rounded-lg hover:bg-pink-600 hover:text-white text-gray-400 transition-all transform hover:-translate-y-1"><Instagram size={18} /></a>
                <a href="https://www.linkedin.com/company/efftech-companyltd/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="bg-gray-800 p-2.5 rounded-lg hover:bg-blue-600 hover:text-white text-gray-400 transition-all transform hover:-translate-y-1"><Linkedin size={18} /></a>
                <a href="https://x.com/TechEff" target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)" className="bg-gray-800 p-2.5 rounded-lg hover:bg-gray-600 hover:text-white text-gray-400 transition-all transform hover:-translate-y-1"><Twitter size={18} /></a>
                <a href="https://www.youtube.com/@EffTech-u2b" target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="bg-gray-800 p-2.5 rounded-lg hover:bg-red-600 hover:text-white text-gray-400 transition-all transform hover:-translate-y-1"><Youtube size={18} /></a>
                <a href="https://www.tiktok.com/@efftech1" target="_blank" rel="noopener noreferrer" aria-label="TikTok" className="bg-gray-800 p-2.5 rounded-lg hover:bg-gray-900 hover:text-white text-gray-400 transition-all transform hover:-translate-y-1">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                  </svg>
                </a>
                <a href="https://wa.me/231770014544" target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className="bg-gray-800 p-2.5 rounded-lg hover:bg-green-500 hover:text-white text-gray-400 transition-all transform hover:-translate-y-1"><MessageCircle size={18} /></a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-white font-bold text-lg mb-6 flex items-center"><span className="w-8 h-0.5 bg-primary-600 mr-3"></span>Quick Links</h3>
              <ul className="space-y-3">
                {navItems.map(item => (
                  <li key={item.label}>
                    <Link to={item.path} className="hover:text-primary-400 transition-colors flex items-center group">
                      <span className="mr-2 text-primary-800 group-hover:text-primary-500 transition-colors">›</span> {item.label}
                    </Link>
                  </li>
                ))}
                <li>
                  <Link to="/privacy" className="hover:text-primary-400 transition-colors flex items-center group">
                    <span className="mr-2 text-primary-800 group-hover:text-primary-500 transition-colors">›</span> Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="hover:text-primary-400 transition-colors flex items-center group">
                    <span className="mr-2 text-primary-800 group-hover:text-primary-500 transition-colors">›</span> Terms of Service
                  </Link>
                </li>
              </ul>
            </div>

            {/* Resources */}
            <div>
              <h3 className="text-white font-bold text-lg mb-6 flex items-center"><span className="w-8 h-0.5 bg-primary-600 mr-3"></span>Resources</h3>
              <ul className="space-y-3 text-sm">
                <li><Link to="/products" className="hover:text-primary-400 transition-colors">Products & Solutions</Link></li>
                <li><Link to="/testimonials" className="hover:text-primary-400 transition-colors">Client Testimonials</Link></li>
                <li><Link to="/blog" className="hover:text-primary-400 transition-colors">Blog & Insights</Link></li>
                <li><Link to="/help" className="hover:text-primary-400 transition-colors">Help Center</Link></li>
                <li><Link to="/awards" className="hover:text-primary-400 transition-colors">Awards & Recognition</Link></li>
                <li><Link to="/media" className="hover:text-primary-400 transition-colors">Media Center</Link></li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h3 className="text-white font-bold text-lg mb-6 flex items-center"><span className="w-8 h-0.5 bg-primary-600 mr-3"></span>Contact Us</h3>
              <ul className="space-y-5 text-sm">
                <li className="flex items-start space-x-3 group">
                  <MapPin className="mt-1 text-primary-600 group-hover:text-primary-400 transition-colors shrink-0" size={18} />
                  <span className="text-gray-400">Capitol Hill, Monrovia,<br/>Republic of Liberia</span>
                </li>
                <li className="flex items-center space-x-3 group">
                  <Phone className="text-primary-600 group-hover:text-primary-400 transition-colors shrink-0" size={18} />
                  <div className="flex flex-col text-gray-400">
                    <a href="tel:+231770014544" className="hover:text-white transition-colors">(+231) 770-014-544</a>
                    <a href="tel:+231880338949" className="hover:text-white transition-colors">(+231) 880-338-949</a>
                  </div>
                </li>
                <li className="flex items-center space-x-3 group">
                  <Mail className="text-primary-600 group-hover:text-primary-400 transition-colors shrink-0" size={18} />
                  <a href="mailto:efftech1@gmail.com" className="hover:text-white text-gray-400 transition-colors break-all">efftech1@gmail.com</a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-900 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-gray-500">
            <p>&copy; {new Date().getFullYear()} EffTech Limited. All rights reserved.</p>
            <div className="mt-4 md:mt-0 flex space-x-6">
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
              <Link to="/sitemap" className="hover:text-white transition-colors">Sitemap</Link>
              {isAuthenticated && (
                <Link to="/admin" className="text-accent-400 hover:text-accent-300 transition-colors">Admin</Link>
              )}
            </div>
          </div>
        </div>
      </footer>

      {/* Scroll to Top Button */}
      <button
        onClick={scrollToTop}
        className={`fixed bottom-6 right-6 p-3 rounded-full bg-primary-600 text-white shadow-lg z-40 transition-all duration-300 transform hover:bg-primary-700 hover:-translate-y-1 focus:outline-none ${
          showScrollTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'
        }`}
        aria-label="Scroll to top"
      >
        <ArrowUp size={24} />
      </button>
    </div>
  );
}

